import {
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Button,
  CommandList,
  CommandEmpty,
  Command,
  CommandInput,
  CommandItem,
  CommandGroup,
} from "web-design-system";

import { ChevronsUpDown, Loader2, Check } from "lucide-react";
import { useMemo, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import { useSchemes } from "creso-core/hooks";
import { SchemeType, Scheme } from "creso-core/types";
import { AmcLogo } from "@core/components/logo/amc-logo";
import { InView } from "react-intersection-observer";

interface SchemesSearchDropdownProps<T extends SchemeType = "all"> {
  value: { label: string; value: Scheme<T>["scheme_isin"] } | null;
  onChange: (value: Scheme<T> | null) => void;
  placeholder?: string;
  type: T;
  frequency?: string;
  amc_id?: number;
  trigger?: React.ForwardRefExoticComponent<{
    value: { label: string; value: Scheme<T>["scheme_isin"] } | null;
    open: boolean;
    setOpen: (open: boolean) => void;
  }>;
  align?: "start" | "center" | "end";
  disabled?: boolean;
}

export function SchemesSearchDropdown<T extends SchemeType = "all">({
  value,
  onChange,
  placeholder,
  type,
  frequency,
  amc_id,
  trigger: Trigger,
  align = "center",
  disabled = false,
}: SchemesSearchDropdownProps<T>) {
  const [open, setOpen] = useState(false);

  const [search, setSearch] = useState("");

  const [debouncedSearch] = useDebounceValue(search, 500);

  const { schemes, fetchNextPage, hasNextPage, isPlaceholderData } =
    useSchemes<T>({
      type,
      search: debouncedSearch,
      frequency,
      amc_id,
    });

  const schemesOptions = useMemo(() => {
    const options =
      schemes?.map((scheme) => ({
        label: scheme.scheme_name,
        value: scheme.scheme_isin,
        amc_morningstar_code: scheme.amc_morningstar_code,
      })) || [];
    return options;
  }, [schemes]);

  return (
    <Popover modal={true} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {Trigger ? (
          <Trigger value={value ?? null} open={open} setOpen={setOpen} />
        ) : (
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-disabled={disabled} // Use aria-disabled instead of disabled
            className={cn(
              "w-full justify-between shadow-none",
              disabled && "cursor-not-allowed bg-accent pointer-events-none"
            )}
            onClick={(e) => disabled && e.preventDefault()} // Prevent clicks when disabled
          >
            <div
              className={cn(
                "truncate font-normal text-left",
                !value && "text-muted-foreground"
              )}
            >
              {value?.label || placeholder}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className="p-0 w-(--radix-popover-trigger-width) max-w-(--radix-popover-trigger-width)"
      >
        <Command shouldFilter={false}>
          <CommandInput
            value={search}
            onValueChange={(v) => setSearch(v)}
            placeholder="Search scheme..."
          />
          <CommandList className={cn(isPlaceholderData && "opacity-50")}>
            <CommandEmpty>No scheme found</CommandEmpty>
            <CommandGroup>
              {schemesOptions?.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => {
                    onChange(
                      value?.value === option.value
                        ? null
                        : (schemes?.find(
                            (s) => s.scheme_isin === option.value
                          ) ?? null)
                    );
                    setOpen(false);
                  }}
                >
                  <div className="flex flex-row gap-2 items-center">
                    <AmcLogo amc_code={option?.amc_morningstar_code} />
                    {option.label}
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value?.value === option.value
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                  </div>
                </CommandItem>
              ))}
              {hasNextPage && (
                <InView
                  as="div"
                  onChange={(inView) => {
                    if (inView) {
                      fetchNextPage();
                    }
                  }}
                  className="flex justify-center items-center w-full py-2"
                >
                  <Loader2 className="h-4 w-4 animate-spin" />
                </InView>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
